import { motion } from "framer-motion";
import { Star, Sparkles, Heart, MessageCircle, Send, Bookmark, Search, FileText, Check } from "lucide-react";
import { Reveal, Overline } from "@/components/primitives";
import { assets } from "@/config/assets";

/* ---------- Mockups ---------- */

const Highlight = ({ children }) => (
  <span className="rounded-[3px] bg-black px-1 py-0.5 font-medium text-white">{children}</span>
);

const ReplyMockup = () => (
  <div className="w-full max-w-[420px] rounded-[10px] border border-black/10 bg-white p-5 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.4)]">
    {/* review */}
    <div className="rounded-[8px] border border-black/10 bg-base p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-xs font-bold text-white">D</div>
        <div>
          <div className="text-xs font-semibold">Daniel P.</div>
          <div className="flex gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={11} className="fill-black text-black" />
            ))}
          </div>
        </div>
        <span className="ml-auto text-[10px] text-black/40">2h ago</span>
      </div>
      <p className="mt-3 text-xs leading-relaxed text-black/70">
        Best haircut I've had in the Mission District. Friendly staff and super quick.
      </p>
    </div>
    {/* AI reply */}
    <div className="mt-3 rounded-[8px] border border-black/10 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-black">
          <Sparkles size={11} className="text-white" />
        </div>
        <span className="text-[11px] font-semibold">Suggested response</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-black/[0.05] px-2 py-0.5 text-[9px] font-medium text-black/55">
          <Sparkles size={9} /> AI generated
        </span>
      </div>
      <p className="text-xs leading-relaxed text-black/75">
        Thank you, Daniel! We're thrilled you loved your <Highlight>haircut in the Mission District</Highlight>. Our team takes pride in fast, friendly service — see you next time!
      </p>
      <div className="mt-3 flex gap-2">
        <div className="rounded-[6px] bg-black px-3 py-1.5 text-[10px] font-semibold text-white">Post reply</div>
        <div className="rounded-[6px] border border-black/15 px-3 py-1.5 text-[10px] font-medium">Edit</div>
      </div>
    </div>
  </div>
);

const ShareMockup = () => (
  <div className="w-full max-w-[340px] rounded-[10px] border border-black/10 bg-white shadow-[0_30px_70px_-40px_rgba(0,0,0,0.4)]">
    <div className="flex items-center gap-2.5 p-3">
      <div className="h-8 w-8 rounded-full bg-black" />
      <div>
        <div className="text-[11px] font-semibold">bloom.cafe</div>
        <div className="text-[9px] text-black/40">Sponsored · Local</div>
      </div>
      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-black/[0.05] px-2 py-0.5 text-[9px] font-medium text-black/55">
        <Sparkles size={9} /> AI post
      </span>
    </div>
    {/* quote graphic */}
    <div className="mx-3 rounded-[8px] bg-black p-6 text-white">
      <div className="mb-2 flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} size={12} className="fill-white text-white" />
        ))}
      </div>
      <p className="text-sm font-medium leading-snug">
        "Best flat white in town — I come here every morning."
      </p>
      <p className="mt-3 text-[10px] uppercase tracking-[0.16em] text-white/50">— Verified customer</p>
    </div>
    <div className="flex items-center gap-4 p-3 text-black/70">
      <Heart size={16} className="fill-black text-black" />
      <MessageCircle size={16} />
      <Send size={16} />
      <Bookmark size={16} className="ml-auto" />
    </div>
    <div className="px-3 pb-4 text-[11px] leading-snug text-black/70">
      <span className="font-semibold text-black">312 likes</span> · Real words from real regulars. ☕ Come taste why. #localcafe #coffeelovers
    </div>
  </div>
);

const GeoMockup = () => (
  <div className="w-full max-w-[420px] space-y-3">
    <div className="rounded-[10px] border border-black/10 bg-white p-5 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.4)]">
      <div className="mb-3 flex items-center gap-2">
        <FileText size={14} />
        <span className="text-[11px] font-semibold">Generated article</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-black/[0.05] px-2 py-0.5 text-[9px] font-medium text-black/55">
          <Sparkles size={9} /> SEO optimized
        </span>
      </div>
      <div className="text-sm font-bold tracking-tight text-black">
        Where to Find the Best Coffee in the Mission District
      </div>
      <div className="mt-3 space-y-2">
        <div className="h-2 w-full rounded-full bg-black/10" />
        <div className="h-2 w-11/12 rounded-full bg-black/10" />
        <div className="h-2 w-4/5 rounded-full bg-black/10" />
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {["#local-coffee", "#mission-district", "#specialty-roast"].map((t) => (
          <span key={t} className="rounded-[6px] bg-black/[0.05] px-2 py-1 text-[9px] font-medium text-black/60">{t}</span>
        ))}
      </div>
    </div>
    {/* AI discovery */}
    <div className="rounded-[10px] border border-black/10 bg-black p-4 text-white">
      <div className="mb-3 flex items-center gap-2">
        <Search size={13} />
        <span className="text-[11px] font-semibold">Discovered by AI search</span>
      </div>
      <div className="space-y-2">
        {["ChatGPT", "Gemini", "Perplexity"].map((ai) => (
          <div key={ai} className="flex items-center justify-between rounded-[6px] border border-white/10 px-3 py-1.5 text-[10px]">
            <span className="font-medium text-white/85">{ai}</span>
            <span className="inline-flex items-center gap-1 text-white/55"><Check size={10} /> Recommended</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ---------- Section ---------- */

const AGENTS = [
  {
    portrait: assets.agents.reply,
    label: "Reply Agent",
    desc: "Analyzes customer reviews and automatically generates professional responses designed to strengthen trust, reinforce local relevance, and keep the business profile consistently active. It naturally incorporates services, locations, and customer sentiment while keeping an authentic human tone.",
    result: "A stronger online reputation, increased customer trust, and a more active Google Business presence.",
    mockup: <ReplyMockup />,
  },
  {
    portrait: assets.agents.share,
    label: "Share Agent",
    desc: "Transforms customer reviews into ready-to-publish social content automatically — generating captions, quote graphics, and testimonial posts tailored for modern social platforms. It keeps businesses consistently visible without creating content by hand every day.",
    result: "More social proof, stronger engagement, and a consistently active online brand presence.",
    mockup: <ShareMockup />,
  },
  {
    portrait: assets.agents.geo,
    label: "GEO Agent",
    desc: "Uses customer review language, business information, and local search intent to generate optimized website and blog content for both traditional search engines and AI-powered discovery platforms. It continuously expands your digital footprint using real customer experiences as visibility signals.",
    result: "Improved local visibility across Google and modern AI search experiences like ChatGPT, Gemini, and Perplexity.",
    mockup: <GeoMockup />,
  },
];

export default function AIAgents() {
  return (
    <section id="agents" className="relative bg-base py-24 lg:py-32">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Overline>Your AI growth team</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-4xl font-black leading-[1.02] tracking-tighter text-black sm:text-5xl lg:text-6xl">
              A team of AI agents working while you work
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-black/60">
              Specialized AI agents help transform customer feedback into visibility,
              engagement, and long-term growth — automatically.
            </p>
          </Reveal>
        </div>

        <div className="mt-20 space-y-24 lg:space-y-32">
          {AGENTS.map((a, i) => {
            const reversed = i % 2 === 1;
            return (
              <div
                key={a.label}
                className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20"
              >
                {/* Text side */}
                <Reveal className={reversed ? "lg:order-2" : ""}>
                  <div className="relative inline-block">
                    <div className="absolute -inset-3 rounded-full bg-black/[0.06] blur-xl" />
                    <img
                      src={a.portrait}
                      alt=""
                      className="relative h-20 w-20 rounded-full border border-black/10 object-cover"
                    />
                  </div>
                  <p className="mt-8 text-xl font-light leading-relaxed text-black/80 sm:text-2xl">
                    {a.desc}
                  </p>
                  <div className="mt-8 flex items-start gap-3 rounded-[10px] border border-black/10 bg-white p-5">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] bg-black">
                      <Sparkles size={12} className="text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-black/45">Result</div>
                      <p className="mt-1 text-sm font-medium leading-relaxed text-black">{a.result}</p>
                    </div>
                  </div>
                </Reveal>

                {/* Mockup side */}
                <Reveal
                  delay={0.1}
                  className={`flex justify-center ${reversed ? "lg:order-1 lg:justify-start" : "lg:justify-end"}`}
                >
                  <motion.div
                    initial={{ rotate: reversed ? 2 : -2 }}
                    whileHover={{ rotate: 0, y: -6 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                  >
                    {a.mockup}
                  </motion.div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
